import { Link, useSearchParams } from 'react-router-dom';

import { IconPlus } from '../../../common/icons';
import { Gate } from '../../../global/contexts/auth/auth';
import ProjectFilter from '../../projects/layouts/project-filter';
import { TICKETS_PAGE_SIZE, useTicketTally, useTicketsList } from '../contexts/tickets';
import { TICKET_KINDS, TICKET_STATES } from '../utils/tickets';
import TicketRow from './ticket';

import '../styles/tickets.css';

// La ventana de tickets: lo que alguien reportó, y en qué quedó.
//
// LOS TRES FILTROS SON DEL SERVIDOR, los tres. `GET /admin/tickets` toma
// `project_id`, `state` y `kind`, así que la paginación y el total siguen
// diciendo la verdad cuando se filtra — al revés que la pila de hallazgos, que
// tiene que separar en el navegador y avisarlo. Acá no hay nada que avisar
// porque no se esconde nada por el camino.
//
// LO QUE NO HAY ES ORDEN, y no se inventa uno. El backend devuelve `ORDER BY
// k.id DESC` fijo, sin parámetro. Un selector de «más antiguo primero» solo
// podría dar vuelta la página ya cargada, o sea el más viejo DE LOS 25 MÁS
// NUEVOS — que para una cola de triage, donde importa lo que lleva más tiempo
// esperando, es directamente la respuesta equivocada. Mejor una pantalla que
// ordena de una sola forma y lo dice, que un control que parece contestar la
// pregunta y contesta otra. Queda anotado como pendiente del backend.

export default function TicketsWindow() {
    const [params, setParams] = useSearchParams();

    const projectId = params.get('project') || '';
    const state = params.get('state') || '';
    const kind = params.get('kind') || '';
    const page = Math.max(0, Number(params.get('page') || 0));

    const setParam = (key, value) => {
        const target = new URLSearchParams(params);
        if (value) target.set(key, value);
        else target.delete(key);
        // Cambiar un filtro vuelve a la primera página: quedarse en la cuarta
        // de un listado que ahora tiene dos es una pantalla vacía que parece un
        // error.
        if (key !== 'page') target.delete('page');
        setParams(target);
    };

    const { data, isLoading, isFetching, error } = useTicketsList({
        limit: TICKETS_PAGE_SIZE,
        offset: page * TICKETS_PAGE_SIZE,
        projectId,
        state,
        kind,
    });

    const tally = useTicketTally({ projectId });

    const items = data?.items ?? [];
    const total = data?.pagination?.total ?? 0;
    const lastPage = Math.max(0, Math.ceil(total / TICKETS_PAGE_SIZE) - 1);

    if (error) {
        return (
            <p className="tickets__error">
                {error.message || 'No se pudieron cargar los tickets'}
            </p>
        );
    }

    return (
        <section className="tickets">
            <header className="tickets__header">
                <div className="tickets__heading">
                    <span className="tickets__hint">Lo que reportaron</span>
                    <h3 className="tickets__title">Tickets</h3>
                </div>

                {/* `tasks:write` y no un código propio: no hay `tickets:*` en
                    core/authz/roles.js — las cuatro rutas de tickets piden los
                    permisos de tarea. */}
                <Gate domain="tasks:write">
                    <Link to="/admin/operation/tickets/new" className="tickets__new">
                        <IconPlus />
                        Registrar un reporte
                    </Link>
                </Gate>
            </header>

            {/* EL RECUENTO ES DEL SISTEMA, NO DE LA PÁGINA. Son tres consultas
                de una fila que solo leen el total, con el mismo alcance y el
                mismo proyecto elegido. Contarlo sobre los 25 cargados habría
                sido gratis y habría contestado otra pregunta.

                Y cada uno es un botón: el recuento de «recibidos» es la puerta
                a los recibidos. Un número que dice cuánto hay pendiente y no
                lleva a verlo obliga a repetirlo en el selector de al lado. */}
            <ul className="tickets__tally">
                {tally.map(step => (
                    <li key={step.key} className={'tally tally--' + step.tone}>
                        <button
                            type="button"
                            className={'tally__button' + (state === step.key ? ' tally__button--picked' : '')}
                            onClick={() => setParam('state', state === step.key ? '' : step.key)}
                            aria-pressed={state === step.key}
                        >
                            <span className="tally__count">
                                {step.isLoading ? '·' : step.total}
                            </span>
                            <span className="tally__label">{step.label}</span>
                        </button>
                    </li>
                ))}
            </ul>

            <div className="tickets__filters">
                <ProjectFilter
                    value={projectId}
                    onChange={(id) => setParam('project', id)}
                />

                <label className="tickets__field">
                    <span className="tickets__field-label">Estado</span>
                    <select
                        className="tickets__select"
                        value={state}
                        onChange={e => setParam('state', e.target.value)}
                    >
                        <option value="">Todos</option>
                        {TICKET_STATES.map(item => (
                            <option key={item.key} value={item.key}>{item.label}</option>
                        ))}
                    </select>
                </label>

                <label className="tickets__field">
                    <span className="tickets__field-label">Tipo</span>
                    <select
                        className="tickets__select"
                        value={kind}
                        onChange={e => setParam('kind', e.target.value)}
                    >
                        <option value="">Todos</option>
                        {TICKET_KINDS.map(item => (
                            <option key={item.key} value={item.key}>{item.label}</option>
                        ))}
                    </select>
                </label>
            </div>

            {isLoading && <p className="tickets__empty">Cargando…</p>}

            {!isLoading && !items.length && (
                <p className="tickets__empty">
                    {projectId || state || kind
                        ? 'Ningún ticket con esos filtros.'
                        : 'Todavía no hay tickets. Nadie reportó nada, o nadie lo cargó acá.'}
                </p>
            )}

            {items.length > 0 && (
                <ul className={'tickets__list' + (isFetching ? ' tickets__list--busy' : '')}>
                    {items.map(ticket => (
                        <TicketRow key={ticket.id} ticket={ticket} />
                    ))}
                </ul>
            )}

            <footer className="tickets__foot">
                <p className="tickets__note">
                    Un ticket es lo que alguien reportó, tal como lo reportó.{' '}
                    <strong>Recibirlo no confirma nada</strong>: nace sin clasificar, y decir
                    qué es —un error, una mejora, algo que no se pudo reproducir— es una
                    operación aparte, con quién la hizo y cuándo. Que sea un problema o una
                    sugerencia es un dato y no dispara nada: ningún ticket se convierte solo
                    en un incidente ni en una tarea.
                </p>

                <p className="tickets__note">
                    El orden es fijo, del más nuevo al más viejo: el listado del backend no
                    acepta un parámetro de orden, así que esta ventana no ofrece uno que solo
                    podría dar vuelta la página que ya trajo.
                </p>

                {total > 0 && (
                    <div className="tickets__pager">
                        <button
                            type="button"
                            className="tickets__page"
                            onClick={() => setParam('page', String(page - 1))}
                            disabled={page === 0}
                        >
                            Anterior
                        </button>
                        <span className="tickets__count">
                            {page * TICKETS_PAGE_SIZE + 1}–{Math.min((page + 1) * TICKETS_PAGE_SIZE, total)} de {total}
                        </span>
                        <button
                            type="button"
                            className="tickets__page"
                            onClick={() => setParam('page', String(page + 1))}
                            disabled={page >= lastPage}
                        >
                            Siguiente
                        </button>
                    </div>
                )}
            </footer>
        </section>
    );
}
